import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PagePanel,
  Box,
  TransferMoneyIcon,
  LoadingSpinner,
  IPagePanelFooter,
  GuideDot,
  BenefitType
} from 'edenred-ui';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';

import { useMenuDimensions, useOnboardingWizard } from '@epi-shared/hooks';
import { getCompanyAccounts } from '@epi-selectors/companyAccounts';
import { getCompanyBeneficariesRequest } from '@epi-api/companyBeneficiaries';
import { EmptyState, TransferPageHeader } from '@epi-shared/components';
import { OnboardingSupportInformation } from '@epi-components/OnboardingSupportInformation';
import { goToPage } from '@epi-actions/navigation';
import { routerPaths } from '@epi-constants/routerPaths';
import { loadCompanyAccounts, getUserCompany } from '@epi-actions/api';
import { loadCompanyAccounts as loadCompanyAccountsSelector } from '@epi-selectors/loading';
import OnboardingStepper from '@epi-components/OnboardingStepper/OnboardingStepper';
import { FormFields } from '@epi-forms/helpers';
import { selectActiveBenefits } from '@epi-selectors/benefitGroupSettings';
import { getOnboardingLoadBenefitPageType } from '@epi-store/onboarding/onboarding.selectors';
import { LoadBenefitPageTypes } from '@epi-constants/onboardingTypes';
import { getLoginState } from '@epi-selectors/login';
import { useGetDetailsQuery } from '@epi-api/user';

import { AdminTopup, Topup } from './components';
import { useTopupFormContext } from './hooks';

const GuideDotContainer = styled.div`
  position: absolute;
  bottom: 140px;
  left: 50%;
  transform: translateX(calc(-50% + 310px));
  z-index: 1;
`;

export function LoadBenefit() {
  const { t } = useTranslation();
  const { isMobile } = useMenuDimensions();
  const dispatch = useDispatch();
  const { isOnBoardingActive } = useOnboardingWizard();
  const { isUserImpersonate } = useSelector(getLoginState);
  const [footer, setFooter] = useState<IPagePanelFooter>();
  const [isBeneficiariesLoaded, setIsBeneficiariesLoaded] =
    useState<boolean>(false);
  const [hasBeneficiaries, setHasBeneficiaries] = useState<boolean>(false);
  const isLoading = useSelector(loadCompanyAccountsSelector);
  const accounts = useSelector(getCompanyAccounts);
  const { form } = useTopupFormContext();
  const { watch } = form;
  const benefitType = watch(FormFields.Benefit);
  const activeBenefits = useSelector(selectActiveBenefits);
  const onboardingPageType = useSelector(getOnboardingLoadBenefitPageType);
  const { isLoading: isCustomerDataLoading, data: customerData } =
    useGetDetailsQuery(undefined, {
      refetchOnMountOrArgChange: false
    });

  const showGuidingDot =
    isOnBoardingActive &&
    footer &&
    !footer?.disableCancel &&
    benefitType !== BenefitType.lunch &&
    activeBenefits.includes(BenefitType.lunch) &&
    onboardingPageType !== LoadBenefitPageTypes.Lounari;

  const loadBeneficiaries = async (): Promise<void> => {
    const response = await getCompanyBeneficariesRequest();
    setIsBeneficiariesLoaded(true);
    setHasBeneficiaries(response.beneficiaries.length > 0);
  };

  const navigateToEmployees = (): void => {
    dispatch(goToPage(routerPaths.beneficiaries));
  };

  useEffect(() => {
    if (!isUserImpersonate && customerData?.isPbu)
      dispatch(goToPage(routerPaths.home));
    loadBeneficiaries();
    dispatch(loadCompanyAccounts());
    dispatch(getUserCompany());
  }, []);

  const renderPageContent = () => {
    if (
      (isLoading && accounts.length === 0) ||
      !isBeneficiariesLoaded ||
      isCustomerDataLoading
    ) {
      return <LoadingSpinner />;
    }

    if (!hasBeneficiaries) {
      return (
        <Box minHeight={590}>
          <EmptyState
            icon={<TransferMoneyIcon />}
            title={t('transfer_money.add_employees_title')}
            description={t('transfer_money.add_employees_description')}
            btnText={t('transfer_money.add_employees_btn')}
            btnOnClick={navigateToEmployees}
          />
        </Box>
      );
    }

    if (isUserImpersonate && customerData?.isPbu) {
      return <AdminTopup updateFooter={setFooter} />;
    }

    return <Topup updateFooter={setFooter} />;
  };

  return (
    <Box>
      <OnboardingStepper />
      <PagePanel
        title={t(
          isUserImpersonate && customerData?.isPbu
            ? 'containers.load_benefit.admin_header'
            : 'containers.load_benefit.header'
        )}
        footer={footer}
        headerContent={!isMobile && <TransferPageHeader />}
      >
        {renderPageContent()}
      </PagePanel>
      <OnboardingSupportInformation />
      {showGuidingDot && (
        <Box position="sticky" bottom={-104} left={0} zIndex={1}>
          <GuideDotContainer>
            <GuideDot
              storageKey="skip-load-guide-dot"
              tooltipDescription={t('guided_tour.skip_load')}
              tooltipSide="top"
            />
          </GuideDotContainer>
        </Box>
      )}
    </Box>
  );
}
